import { useMutation } from '@tanstack/react-query';
import { updateOnboarding } from '../../clients/onboardingClient';
import type { Step, StepRuntime } from '../types/types';
import { StepSlug } from '../types/types';

function RecordingOptionsStep({ next }: StepRuntime): JSX.Element {
  const recordingOptionsMutation = useMutation({
    mutationFn: updateOnboarding,
    onSettled: () => {
      // Add a small delay before advancing to the next page
      setTimeout(() => {
        next(StepSlug.Features);
      }, 100); // 0.10 second delay
    }
  });

  function handleContinue() {
    recordingOptionsMutation.mutate({
      progress: 2
    });
  }

  const Check = ({ children }: { children: React.ReactNode }) => (
    <li className="flex items-start gap-3">
      <svg width="16" height="16" viewBox="0 0 20 20" fill="none" className="mt-0.5 shrink-0">
        <path
          d="M16.667 5.833l-8.334 8.334L3.333 9.167"
          stroke="#16a34a"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span>{children}</span>
    </li>
  );

  const Card = ({
    title,
    children,
    icon
  }: {
    title: string;
    children: React.ReactNode;
    icon?: React.ReactNode;
  }) => (
    <div className="w-full max-w-[400px] lg:w-[400px] h-[400px] rounded-[24px] border border-gray-200 bg-white shadow-sm p-8 flex flex-col items-center text-center">
      <div className="mb-4 flex items-center justify-center h-8">{icon}</div>
      <h3 className="text-2xl font-semibold text-gray-900 mb-6">{title}</h3>
      {children}
    </div>
  );

  const ORDivider = () => (
    <div className="flex items-center text-gray-400 font-semibold mx-8 my-6 lg:my-0">OR</div>
  );

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-[url('./images/grid.png')] bg-repeat bg-[length:500px] px-6">
      <div className="max-w-6xl w-full text-center">
        <h1 className="text-3xl font-semibold text-gray-900 mb-2">Record meetings with Notewell</h1>
        <p className="text-gray-400 font-medium mb-10">
          Record in our app or upload your meetings. We’ll handle the notes.
        </p>

        <div className="flex flex-col lg:flex-row items-center lg:items-stretch justify-center gap-0">
          {/* Left card: iOS & Android */}
          <Card
            title="iOS & Android"
            icon={
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect x="7" y="2" width="10" height="20" rx="3" stroke="#4F46E5" strokeWidth="2" />
                <circle cx="12" cy="18" r="1" fill="#4F46E5" />
              </svg>
            }
          >
            <div className="w-24 h-24 rounded-md border border-gray-300 grid place-items-center mb-2">
              <img
                src={require('../../images/notewell_mobile_qr.png')}
                alt="QR Code"
                className="rounded-sm w-full h-full object-cover"
              />
            </div>
            <p className="text-sm text-gray-500 mb-6">Scan to download</p>
            <div className="flex-1" />
            <div className="w-full border-t border-gray-200 my-6" />
            <ul className="w-full space-y-3 text-gray-800 text-[15px] text-left">
              <Check>Record in-person or virtual meetings</Check>
              <Check>Review notes on the go</Check>
            </ul>
          </Card>

          <ORDivider />

          {/* Right card: Upload a Recording */}
          <Card
            title="Upload a Recording"
            icon={
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M12 16V4" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" />
                <path
                  d="M8 8l4-4 4 4"
                  stroke="#4F46E5"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <rect x="3" y="12" width="18" height="8" rx="2" stroke="#4F46E5" strokeWidth="2" />
              </svg>
            }
          >
            <div className="flex items-center justify-center w-full mb-4">
              <div className="flex flex-col items-center justify-center w-1/2 border-2 border-gray-300 border-dashed rounded-lg bg-gray-50">
                <div className="flex flex-col items-center justify-center pt-4 pb-4">
                  <svg
                    className="w-6 h-6 mb-3 text-gray-500"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 20 16"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                    />
                  </svg>
                  <p className="text-sm text-gray-500 font-semibold">Upload files</p>
                  <p className="text-xs text-gray-400 mt-1">MP3, MP4, M4A, WEBM</p>
                </div>
              </div>
            </div>
            <div className="flex-1" />
            <div className="w-full border-t border-gray-200 my-6" />
            <ul className="w-full space-y-3 text-gray-800 text-[15px] text-left">
              <Check>Use existing audio or video files</Check>
              <Check>We'll transcribe and draft notes</Check>
            </ul>
          </Card>
        </div>

        <button
          type="button"
          className="mt-12 text-sm font-semibold text-indigo-600 hover:opacity-100 transition duration-300 opacity-70"
          onClick={handleContinue}
        >
          Continue ››
        </button>
      </div>
    </div>
  );
}

export const RecordingOptionsStepModule: Step = {
  slug: StepSlug.RecordingOptions,
  title: 'Recording Options',
  Component: RecordingOptionsStep
};
